import { Injectable } from '@angular/core';
import { DocumentNode, FetchResult } from '@apollo/client/core';
import { DigitaiCreateFleetAircraftEngine, DigitaiFleetAircraftEngine, DigitaiUpdateFleetAircraftEngineById, DigitaiUpdateFleetAircraftEngines } from '@apps/digitai';
import { createMutation, deleteByIdMutation, deleteMutation, fields, findByIdQuery, findQuery, getQuery, insertMutation, paginationQuery, updateByIdMutation, updateMutation } from '@apps/digitai/fleet-aircraft-engine';
import { GraphQLHeaders, GraphQLService, GridData, parseGqlFields, QueryStatement } from '@aurora';
import { BehaviorSubject, first, map, Observable, tap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class FleetAircraftEngineService
{
    paginationSubject$: BehaviorSubject<GridData<DigitaiFleetAircraftEngine> | null> = new BehaviorSubject(null);
    fleetAircraftEngineSubject$: BehaviorSubject<DigitaiFleetAircraftEngine | null> = new BehaviorSubject(null);
    fleetAircraftEnginesSubject$: BehaviorSubject<DigitaiFleetAircraftEngine[] | null> = new BehaviorSubject(null);

    // scoped subjects
    paginationScoped: { [key: string]: BehaviorSubject<GridData<DigitaiFleetAircraftEngine> | null>; } = {};
    fleetAircraftEngineScoped: { [key: string]: BehaviorSubject<DigitaiFleetAircraftEngine | null>; } = {};
    fleetAircraftEnginesScoped: { [key: string]: BehaviorSubject<DigitaiFleetAircraftEngine[] | null>; } = {};

    constructor(
        private readonly graphqlService: GraphQLService,
    ) {}

    /**
    * Getters
    */
    get pagination$(): Observable<GridData<DigitaiFleetAircraftEngine>>
    {
        return this.paginationSubject$.asObservable();
    }

    get fleetAircraftEngine$(): Observable<DigitaiFleetAircraftEngine>
    {
        return this.fleetAircraftEngineSubject$.asObservable();
    }

    get fleetAircraftEngines$(): Observable<DigitaiFleetAircraftEngine[]>
    {
        return this.fleetAircraftEnginesSubject$.asObservable();
    }

    // allows to store different types of pagination under different scopes this allows us
    // to have multiple observables with different streams of pagination data.
    setScopePagination(scope: string, pagination: GridData<DigitaiFleetAircraftEngine>): void
    {
        if (this.paginationScoped[scope])
        {
            this.paginationScoped[scope].next(pagination);
            return;
        }
        // create new subject if not exist
        this.paginationScoped[scope] = new BehaviorSubject(pagination);
    }

    // get pagination observable by scope
    getScopePagination(scope: string): Observable<GridData<DigitaiFleetAircraftEngine>>
    {
        if (this.paginationScoped[scope]) return this.paginationScoped[scope].asObservable();

        this.paginationScoped[scope] = new BehaviorSubject(null);
        return this.paginationScoped[scope].asObservable();
    }

    setScopeFleetAircraftEngine(scope: string, object: DigitaiFleetAircraftEngine): void
    {
        if (this.fleetAircraftEngineScoped[scope])
        {
            this.fleetAircraftEngineScoped[scope].next(object);
            return;
        }
        // create new subject if not exist
        this.fleetAircraftEngineScoped[scope] = new BehaviorSubject(object);
    }

    getScopeFleetAircraftEngine(scope: string): Observable<DigitaiFleetAircraftEngine>
    {
        if (this.fleetAircraftEngineScoped[scope]) return this.fleetAircraftEngineScoped[scope].asObservable();

        this.fleetAircraftEngineScoped[scope] = new BehaviorSubject(null);
        return this.fleetAircraftEngineScoped[scope].asObservable();
    }

    setScopeFleetAircraftEngines(scope: string, objects: DigitaiFleetAircraftEngine[]): void
    {
        if (this.fleetAircraftEnginesScoped[scope])
        {
            this.fleetAircraftEnginesScoped[scope].next(objects);
            return;
        }
        // create new subject if not exist
        this.fleetAircraftEnginesScoped[scope] = new BehaviorSubject(objects);
    }

    getScopeFleetAircraftEngines(scope: string): Observable<DigitaiFleetAircraftEngine[]>
    {
        if (this.fleetAircraftEnginesScoped[scope]) return this.fleetAircraftEnginesScoped[scope].asObservable();

        this.fleetAircraftEnginesScoped[scope] = new BehaviorSubject(null);
        return this.fleetAircraftEnginesScoped[scope].asObservable();
    }

    pagination(
        {
            graphqlStatement = paginationQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<GridData<DigitaiFleetAircraftEngine>>
    {
        // get result, map ang throw data across observable
        return this.graphqlService
            .client()
            .watchQuery<{ pagination: GridData<DigitaiFleetAircraftEngine>; }>({
                query    : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data.pagination),
                tap(pagination => scope ? this.setScopePagination(scope, pagination) : this.paginationSubject$.next(pagination)),
            );
    }

    findById(
        {
            graphqlStatement = findByIdQuery,
            id = null,
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiFleetAircraftEngine;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiFleetAircraftEngine;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    id,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeFleetAircraftEngine(scope, data.object) : this.fleetAircraftEngineSubject$.next(data.object)),
            );
    }

    find(
        {
            graphqlStatement = findQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiFleetAircraftEngine;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiFleetAircraftEngine;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeFleetAircraftEngine(scope, data.object) : this.fleetAircraftEngineSubject$.next(data.object)),
            );
    }

    get(
        {
            graphqlStatement = getQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        objects: DigitaiFleetAircraftEngine[];
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                objects: DigitaiFleetAircraftEngine[];
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeFleetAircraftEngines(scope, data.objects) : this.fleetAircraftEnginesSubject$.next(data.objects)),
            );
    }

    create<T>(
        {
            graphqlStatement = createMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiCreateFleetAircraftEngine;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    insert<T>(
        {
            graphqlStatement = insertMutation,
            objects = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            objects?: DigitaiCreateFleetAircraftEngine[];
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: objects,
                },
                context: {
                    headers,
                },
            });
    }

    updateById<T>(
        {
            graphqlStatement = updateByIdMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateFleetAircraftEngineById;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    update<T>(
        {
            graphqlStatement = updateMutation,
            object = null,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateFleetAircraftEngines;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    deleteById<T>(
        {
            graphqlStatement = deleteByIdMutation,
            id = null,
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    id,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    delete<T>(
        {
            graphqlStatement = deleteMutation,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }
}
